import React, { useEffect, useState } from "react";
import {
    adminListMessages,
    adminToggleAnswered,
    adminDeleteMessage,
    adminStats,
    fetchClips,
    adminCreateClip,
    adminDeleteClip,
} from "../lib/api";
import { Crown } from "../components/CrownIcon";
import { CommsHub } from "../components/CommsHub";
import { QAAdminPanel } from "../components/QAAdminPanel";
import { Check, Trash2, LogOut, Mail, RefreshCw, Film, Inbox, Plus, Radio, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";

const KEY_STORAGE = "gsb_admin_key";

export default function Admin() {
    const [key, setKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
    const [authed, setAuthed] = useState(false);
    const [tab, setTab] = useState("inbox");
    const [messages, setMessages] = useState([]);
    const [clips, setClips] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all");
    const [newClip, setNewClip] = useState({ title: "", video_url: "", poster_url: "" });

    const load = async (k) => {
        const adminKey = k ?? key;
        if (!adminKey) return;
        setLoading(true);
        try {
            const [msgs, s, c] = await Promise.all([
                adminListMessages(adminKey),
                adminStats(adminKey),
                fetchClips(),
            ]);
            setMessages(msgs);
            setStats(s);
            setClips(c);
            setAuthed(true);
            localStorage.setItem(KEY_STORAGE, adminKey);
        } catch (e) {
            toast.error("Invalid admin key");
            setAuthed(false);
            localStorage.removeItem(KEY_STORAGE);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (key) load(key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = (e) => {
        e.preventDefault();
        load(key);
    };

    const toggleAnswered = async (id, current) => {
        try {
            const updated = await adminToggleAnswered(id, !current, key);
            setMessages((m) => m.map((x) => (x.id === id ? updated : x)));
            adminStats(key).then(setStats).catch(() => {});
        } catch {
            toast.error("Failed to update");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this message?")) return;
        try {
            await adminDeleteMessage(id, key);
            setMessages((m) => m.filter((x) => x.id !== id));
            adminStats(key).then(setStats).catch(() => {});
            toast.success("Deleted");
        } catch {
            toast.error("Failed to delete");
        }
    };

    const logout = () => {
        localStorage.removeItem(KEY_STORAGE);
        setKey("");
        setAuthed(false);
        setMessages([]);
        setClips([]);
        setStats(null);
    };

    const addClip = async (e) => {
        e.preventDefault();
        if (!newClip.title.trim() || !newClip.video_url.trim()) {
            toast.error("Title and video URL are required");
            return;
        }
        try {
            const created = await adminCreateClip(
                {
                    title: newClip.title.trim(),
                    video_url: newClip.video_url.trim(),
                    poster_url: newClip.poster_url.trim() || null,
                },
                key,
            );
            setClips((c) => [created, ...c]);
            setNewClip({ title: "", video_url: "", poster_url: "" });
            toast.success("Clip added");
        } catch {
            toast.error("Failed to add clip");
        }
    };

    const removeClip = async (id) => {
        if (!confirm("Delete this clip?")) return;
        try {
            await adminDeleteClip(id, key);
            setClips((c) => c.filter((x) => x.id !== id));
            toast.success("Clip deleted");
        } catch {
            toast.error("Failed to delete clip");
        }
    };

    const filtered = messages.filter((m) =>
        filter === "all" ? true : filter === "unanswered" ? !m.answered : m.answered,
    );

    if (!authed) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <form
                    data-testid="admin-login"
                    onSubmit={submit}
                    className="w-full max-w-md bg-surface border-4 border-black brutal-shadow-pink p-8 space-y-5"
                >
                    <div className="flex items-center gap-3">
                        <Crown className="w-10 h-8" color="#53FC18" />
                        <h1 className="font-anton uppercase text-3xl text-white tracking-tight">
                            Admin Inbox
                        </h1>
                    </div>
                    <p className="font-chivo text-white/60 text-sm">
                        Enter your admin key to view incoming DMs and questions.
                    </p>
                    <input
                        data-testid="admin-key-input"
                        type="password"
                        placeholder="Admin key"
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                        className="w-full bg-black border-2 border-white/15 px-4 py-3 font-chivo text-white focus:border-kickgreen focus:outline-none"
                    />
                    <button
                        data-testid="admin-login-btn"
                        disabled={loading}
                        className="w-full bg-kickgreen text-black font-anton uppercase tracking-wider text-lg py-3 border-2 border-black hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#FF2E93] transition-all"
                    >
                        {loading ? "Checking…" : "Unlock"}
                    </button>
                    <a
                        href="/"
                        className="block text-center font-chivo text-xs text-white/40 hover:text-white"
                    >
                        ← Back to site
                    </a>
                </form>
            </div>
        );
    }

    return (
        <div data-testid="admin-page" className="min-h-screen">
            <header className="border-b-4 border-kickgreen bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
                    <Crown className="w-9 h-7" color="#FF2E93" />
                    <div>
                        <div className="font-anton uppercase text-2xl text-white tracking-tight">
                            Admin Inbox
                        </div>
                        <div className="font-graffiti text-skyblue text-sm -rotate-2 leading-none">
                            @leonardthepreacher
                        </div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            data-testid="refresh-btn"
                            onClick={() => load()}
                            className="inline-flex items-center gap-2 border-2 border-skyblue text-skyblue font-anton uppercase text-sm px-3 py-2 hover:bg-skyblue hover:text-black transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" /> Refresh
                        </button>
                        <button
                            data-testid="logout-btn"
                            onClick={logout}
                            className="inline-flex items-center gap-2 border-2 border-hotpink text-hotpink font-anton uppercase text-sm px-3 py-2 hover:bg-hotpink hover:text-black transition-colors"
                        >
                            <LogOut className="w-4 h-4" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        data-testid="tab-inbox"
                        onClick={() => setTab("inbox")}
                        className={`inline-flex items-center gap-2 font-anton uppercase tracking-wider text-sm px-4 py-2 border-2 border-black transition-all ${
                            tab === "inbox"
                                ? "bg-kickgreen text-black"
                                : "bg-black text-white hover:bg-white/10"
                        }`}
                    >
                        <Inbox className="w-4 h-4" /> Inbox
                    </button>
                    <button
                        data-testid="tab-hub"
                        onClick={() => setTab("hub")}
                        className={`inline-flex items-center gap-2 font-anton uppercase tracking-wider text-sm px-4 py-2 border-2 border-black transition-all ${
                            tab === "hub"
                                ? "bg-skyblue text-black"
                                : "bg-black text-white hover:bg-white/10"
                        }`}
                    >
                        <Radio className="w-4 h-4" /> Comms Hub
                    </button>
                    <button
                        data-testid="tab-qa"
                        onClick={() => setTab("qa")}
                        className={`inline-flex items-center gap-2 font-anton uppercase tracking-wider text-sm px-4 py-2 border-2 border-black transition-all ${
                            tab === "qa"
                                ? "bg-hotpink text-white"
                                : "bg-black text-white hover:bg-white/10"
                        }`}
                    >
                        <MessageCircleQuestion className="w-4 h-4" /> Q&amp;A
                    </button>
                    <button
                        data-testid="tab-clips"
                        onClick={() => setTab("clips")}
                        className={`inline-flex items-center gap-2 font-anton uppercase tracking-wider text-sm px-4 py-2 border-2 border-black transition-all ${
                            tab === "clips"
                                ? "bg-hotpink text-white"
                                : "bg-black text-white hover:bg-white/10"
                        }`}
                    >
                        <Film className="w-4 h-4" /> Clips ({clips.length})
                    </button>
                </div>

                {tab === "hub" && <CommsHub adminKey={key} />}
                {tab === "qa" && <QAAdminPanel adminKey={key} />}

                {tab === "inbox" && stats && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <StatCard label="Total messages" value={stats.total} tone="green" />
                        <StatCard
                            label="Unanswered"
                            value={stats.unanswered}
                            tone="pink"
                        />
                        <StatCard
                            label="Answered"
                            value={stats.total - stats.unanswered}
                            tone="blue"
                        />
                    </div>
                )}

                {tab === "inbox" && (
                <div className="flex items-center gap-2 flex-wrap">
                    {["all", "unanswered", "answered"].map((f) => (
                        <button
                            key={f}
                            data-testid={`filter-${f}`}
                            onClick={() => setFilter(f)}
                            className={`font-anton uppercase tracking-wider text-xs px-4 py-2 border-2 border-black transition-all ${
                                filter === f
                                    ? "bg-kickgreen text-black"
                                    : "bg-black text-white hover:bg-white/10"
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
                )}

                {tab === "clips" && (
                    <ClipsAdminPanel
                        clips={clips}
                        newClip={newClip}
                        setNewClip={setNewClip}
                        onAdd={addClip}
                        onDelete={removeClip}
                    />
                )}

                {tab === "inbox" && (
                <div className="space-y-4">
                    {filtered.length === 0 ? (
                        <div className="bg-surface border-2 border-white/10 p-12 text-center font-chivo text-white/50">
                            No messages here.
                        </div>
                    ) : (
                        filtered.map((m) => (
                            <article
                                key={m.id}
                                data-testid="admin-message"
                                className={`bg-surface border-4 border-black p-5 md:p-6 ${
                                    m.answered ? "brutal-shadow-green" : "brutal-shadow-pink"
                                }`}
                            >
                                <header className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                    <div>
                                        <div className="font-anton uppercase text-xl text-white tracking-tight">
                                            {m.name}
                                        </div>
                                        {m.email && (
                                            <a
                                                href={`mailto:${m.email}`}
                                                className="inline-flex items-center gap-1 font-chivo text-xs text-skyblue hover:underline"
                                            >
                                                <Mail className="w-3 h-3" /> {m.email}
                                            </a>
                                        )}
                                        <div className="font-chivo text-xs text-white/40 mt-1">
                                            {new Date(m.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            data-testid="toggle-answered-btn"
                                            onClick={() => toggleAnswered(m.id, m.answered)}
                                            className={`inline-flex items-center gap-1 font-anton uppercase text-xs px-3 py-2 border-2 border-black ${
                                                m.answered
                                                    ? "bg-kickgreen text-black"
                                                    : "bg-white text-black hover:bg-kickgreen"
                                            }`}
                                        >
                                            <Check className="w-3 h-3" />
                                            {m.answered ? "Answered" : "Mark answered"}
                                        </button>
                                        <button
                                            data-testid="delete-message-btn"
                                            onClick={() => remove(m.id)}
                                            className="inline-flex items-center gap-1 font-anton uppercase text-xs px-3 py-2 border-2 border-black bg-hotpink text-white hover:bg-black hover:text-hotpink"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                </header>
                                <p className="font-chivo text-white/90 whitespace-pre-wrap leading-relaxed">
                                    {m.message}
                                </p>
                            </article>
                        ))
                    )}
                </div>
                )}
            </main>
        </div>
    );
}

const ClipsAdminPanel = ({ clips, newClip, setNewClip, onAdd, onDelete }) => (
    <div className="space-y-6">
        <form
            data-testid="clip-add-form"
            onSubmit={onAdd}
            className="bg-surface border-4 border-black brutal-shadow-blue p-5 md:p-6 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end"
        >
            <div>
                <label className="block font-anton uppercase text-xs tracking-widest text-white/60 mb-1">
                    Title *
                </label>
                <input
                    data-testid="clip-title-input"
                    value={newClip.title}
                    onChange={(e) => setNewClip({ ...newClip, title: e.target.value })}
                    placeholder="Clip title"
                    className="w-full bg-black border-2 border-white/15 px-3 py-2 font-chivo text-white focus:border-kickgreen focus:outline-none"
                />
            </div>
            <div>
                <label className="block font-anton uppercase text-xs tracking-widest text-white/60 mb-1">
                    Video URL *
                </label>
                <input
                    data-testid="clip-video-url-input"
                    value={newClip.video_url}
                    onChange={(e) => setNewClip({ ...newClip, video_url: e.target.value })}
                    placeholder="https://… or /clips/file.mp4"
                    className="w-full bg-black border-2 border-white/15 px-3 py-2 font-chivo text-white focus:border-skyblue focus:outline-none"
                />
            </div>
            <div>
                <label className="block font-anton uppercase text-xs tracking-widest text-white/60 mb-1">
                    Poster URL
                </label>
                <input
                    data-testid="clip-poster-url-input"
                    value={newClip.poster_url}
                    onChange={(e) => setNewClip({ ...newClip, poster_url: e.target.value })}
                    placeholder="(optional)"
                    className="w-full bg-black border-2 border-white/15 px-3 py-2 font-chivo text-white focus:border-hotpink focus:outline-none"
                />
            </div>
            <button
                data-testid="clip-add-btn"
                className="inline-flex items-center gap-2 bg-hotpink text-white font-anton uppercase tracking-wider text-sm px-4 py-2 border-2 border-black hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#53FC18] transition-all"
            >
                <Plus className="w-4 h-4" strokeWidth={3} /> Add Clip
            </button>
        </form>

        {clips.length === 0 ? (
            <div className="bg-surface border-2 border-white/10 p-12 text-center font-chivo text-white/50">
                No clips yet.
            </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clips.map((c) => (
                    <article
                        key={c.id}
                        data-testid="admin-clip"
                        className="bg-surface border-4 border-black brutal-shadow-pink overflow-hidden"
                    >
                        <video
                            src={c.video_url.startsWith("http")
                                ? c.video_url
                                : `${process.env.REACT_APP_BACKEND_URL}${c.video_url}`}
                            controls
                            preload="metadata"
                            className="w-full aspect-[9/16] bg-black object-cover"
                        />
                        <div className="p-3 flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <div
                                    className="font-anton uppercase tracking-tight text-white truncate"
                                    title={c.title}
                                >
                                    {c.title}
                                </div>
                                <div className="font-chivo text-xs text-white/40">
                                    {new Date(c.created_at).toLocaleDateString()} ·{" "}
                                    {c.views || 0} views
                                </div>
                            </div>
                            <button
                                data-testid="clip-delete-btn"
                                onClick={() => onDelete(c.id)}
                                className="bg-hotpink text-white p-2 border-2 border-black hover:bg-black hover:text-hotpink"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </article>
                ))}
            </div>
        )}
    </div>
);

const toneMap = {
    green: "border-kickgreen text-kickgreen",
    pink: "border-hotpink text-hotpink",
    blue: "border-skyblue text-skyblue",
};

const StatCard = ({ label, value, tone }) => (
    <div className={`bg-black border-4 ${toneMap[tone]} p-5`}>
        <div className="font-anton text-5xl leading-none">{value}</div>
        <div className="font-chivo text-xs uppercase tracking-widest text-white/60 mt-2">
            {label}
        </div>
    </div>
);
